import {
  getDay,
  getMonth,
  getYear,
  addMonths,
  subMonths,
  startOfMonth,
  lastDayOfMonth,
  addDays,
  subDays,
  format,
  differenceInCalendarDays,
} from 'date-fns';

const dayOfWeekIndex = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

const WEDNESDAY_INDEX = 3;

/**
 * @func getReportPeriod
 * @desc generate the report period (Wednesday morning to the following Tuesday) based on history
 * @var weeksBack the number of weeks ago for a pay period
 * @returns Object {
 *  startDate:  Date,
 *  endDate:    Date,
 *  value:      int,
 *  label:      string, // shown in the dropdown
 * }
 */
export const getReportPeriod = (weeksBack = 0) => {
  /**
   * 1. get todays date
   * 2. find the nearest Wednesday in the past
   * 3. create a pay period using that date and one week further back
   * - ex. Thurs Aug. 15 would create the period: 8-7 through 8-13
   */
  const today = new Date();
  const dayIndex = getDay(today);
  const dayDiff = dayIndex - WEDNESDAY_INDEX;

  const daysToRemove = dayDiff >= 0 ? dayDiff : 7 + dayDiff;

  // get the dates to use for the comparison
  // start: 10-23
  // end: 10-30
  // would get all data from beginning of 10-23 through end of 10-29
  const endOfLastPayPeriod = subDays(today, daysToRemove + weeksBack * 7);
  const startOfLastPayPeriod = subDays(endOfLastPayPeriod, 7);

  const startLabel = format(startOfLastPayPeriod, 'MM.d.y');
  const endLabel = format(subDays(endOfLastPayPeriod, 1), 'MM.d.y');

  return {
    startDate: startOfLastPayPeriod,
    endDate: endOfLastPayPeriod,
    value: weeksBack,
    label: `${startLabel} - ${endLabel}`,
  };
};

/**
 * @func generateRecentReportPeriods
 * @desc generate the last 5 pay periods
 */
export const generateRecentReportPeriods = () => {
  let payPeriods = [];
  for (let i = 0; i < 10; i++) {
    payPeriods = payPeriods.concat(getReportPeriod(i));
  }

  return payPeriods;
};

/**
 * @func getPeriodRange
 * @desc get the start and end date for the next payroll period based on when the last period was completed
 * @param {Date} lastCompletedDate
 */
export const getPeriodRange = lastCompletedDate => {
  /**
   * 1. get today's date
   * 2. determine whether the end date should be today or a week ahead of the start date:
   *  - should be today if the period will be a partial week
   *  - should be a full week if the last completed date was more than a week ago
   */

  const today = new Date();
  const diffInDays = differenceInCalendarDays(today, lastCompletedDate);

  // if payroll was run today, set end to today
  // if payroll was less than 7 days ago, set end to the last completed day (yesterday)
  // else just add 6 days to the last end date (which was exlusive)
  const isPartialPeriod = diffInDays < 7;

  const endDate = addDays(lastCompletedDate, 6);
  // const endDate = isPartialPeriod ? (diffInDays === 0 ? today : subDays(today, 1)) : addDays(lastCompletedDate, 6);

  const range = {
    startDate: lastCompletedDate,
    endDate,
    isPartialPeriod,
  };
  return range;
};

export const generatePeriodLabel = (startDate, endDate) => {
  const startLabel = format(startDate, 'MM.d.y');
  const endLabel = format(endDate, 'MM.d.y');

  return `${startLabel} - ${endLabel}`;
};
